import * as React from 'react';
import { makeStyles, Theme, createStyles, Typography } from '@material-ui/core';
import { useQuery } from 'react-apollo';
import gql from 'graphql-tag';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import AlarmOutlinedIcon from '@material-ui/icons/AlarmOutlined';
import WorkOutlineIcon from '@material-ui/icons/WorkOutline';
import { UserFeature } from '../../../types/UserFeature';
import { SkeletonLoader } from '../../../components/loading/SkeletonLoader';
import { browserHistory } from '../../../components/AppContent';
import { linkToPage, showNotification } from '../../../App';
import EventSystem from '../../../EventSystem';
import { QUICK_VIEW_REFRESH_EVENT } from '../QuickViewDrawer';
import { RefreshingIndicator, runWithMinimumVisibleRefresh } from './RefreshingIndicator';
import { IAutoCompleteItem } from '../../../typings/autoComplete';

// This card's palette is ported from the mobile app's own theme.ts
// (statusPending/statusUrgent for the row tints) rather than this web app's own brand blue,
// except the primary accent (mobile's teal) - explicit request to drop that in favour of this
// web app's own brand blue (theme.palette.primary.main / mainTheme.TemplateColor.Primary,
// src/index.tsx), matching KpiSummaryWidget.tsx's own tint, so all three cards read as one
// consistent accent instead of teal-vs-blue. Kept local to this card (and YourTasksWidget.tsx)
// rather than added to the shared --dc-* tokens, since it's a deliberate visual quote of
// specific mobile screens re-tinted to match, not a palette change for the rest of the app.
// Exported - EnquiryFollowUpsDueWidget.tsx's own card status pills (DueListWidget.tsx) reuse
// this exact palette too, per explicit request to match this widget's colours rather than
// inventing a second one.
export const MOBILE_ACCENT_TEAL = '#5173FF';
export const MOBILE_ACCENT_TEAL_SOFT = '#5173FF24';
export const MOBILE_STATUS_PENDING = '#D98324';
export const MOBILE_STATUS_PENDING_SOFT = '#FCF0DC';
export const MOBILE_STATUS_URGENT = '#E5484D';
export const MOBILE_STATUS_URGENT_SOFT = '#FDE8E8';
// Explicit request ("for assigned, use a green pill") - this widget's own palette above has
// no green (only the accent blue/pending amber/urgent red), so this is a new addition, same
// handpicked-soft-literal style as MOBILE_STATUS_PENDING/_URGENT above rather than the accent
// colour's alpha-suffix trick.
export const MOBILE_STATUS_GREEN = '#1B8A5A';
export const MOBILE_STATUS_GREEN_SOFT = '#E1F5EA';
export const MOBILE_SURFACE_MUTED = '#F7F8FC';
export const MOBILE_BORDER = '#E7E9F2';
export const MOBILE_TEXT_MUTED = '#9AA3B5';

const PULSE_PATH = 'M0,12 H4 L7,4 L10,20 L13,12 H28 L31,4 L34,20 L37,12 H48';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        // Same card construction as the mobile app's EnquiryStatusOverview.tsx: a gradient
        // wash + an oversized low-opacity watermark echo of the header's own icon, bleeding
        // off the top-right corner. Deliberately plain HTML/CSS, not an MUI Card - this is a
        // one-off visual quote of a specific mobile screen, not this app's own card language.
        card: {
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#FFFFFF',
            // Matches the app's own standard card/table radius (App.css's rv-grid-container,
            // the same radius the enquiry/matter/task grids use) - explicit request, so every
            // widget in this panel reads as one consistent radius rather than each card
            // inventing its own.
            borderRadius: 8,
            padding: 20,
            // A stronger, layered "embossed" shadow instead of one faint blur - explicit
            // request ("I want the widgets to pop a bit... doesn't stand out"). Same two-layer
            // recipe (a tight, darker shadow right under the card + a softer, wider one further
            // out) applied consistently across every widget in this panel - see
            // DueListWidget.tsx/KpiSummaryWidget.tsx/QuickActionsWidget.tsx's identical comment.
            boxShadow: '0 1px 3px rgba(16,26,51,0.12), 0 6px 16px rgba(16,26,51,0.08)',
        },
        wash: {
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${MOBILE_ACCENT_TEAL}29 0%, ${MOBILE_ACCENT_TEAL}05 100%)`,
            pointerEvents: 'none',
        },
        watermark: {
            position: 'absolute',
            top: -20,
            right: -22,
            color: MOBILE_ACCENT_TEAL,
            opacity: 0.08,
            pointerEvents: 'none',
        },
        content: {
            position: 'relative',
        },
        headerRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 12,
        },
        headerLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
        },
        headerIconChip: {
            width: 38,
            height: 38,
            minWidth: 38,
            borderRadius: 14,
            backgroundColor: MOBILE_ACCENT_TEAL_SOFT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: '$iconBreathe 2200ms ease-in-out infinite alternate',
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
            },
        },
        '@keyframes iconBreathe': {
            from: { transform: 'scale(1)' },
            to: { transform: 'scale(1.12)' },
        },
        pulseTrace: {
            animation: '$pulseScroll 1600ms linear infinite',
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
            },
        },
        '@keyframes pulseScroll': {
            from: { transform: 'translateX(0)' },
            to: { transform: 'translateX(-24px)' },
        },
        heading: {
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: -0.3,
            color: '#0B1220',
        },
        // "View all" - explicit request, matches EnquiryStatusOverview.tsx's own header link
        // (this widget previously had no equivalent - the big stat number below is new too).
        viewAllLink: {
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
            flexShrink: 0,
        },
        viewAllLabel: {
            fontSize: 12.5,
            fontWeight: 700,
            color: MOBILE_ACCENT_TEAL,
        },
        // Ionicons has no bare diagonal-arrow glyph on mobile either - same trick here, a
        // plain forward arrow rotated -45deg reads as the same "external link" mark.
        viewAllIcon: {
            fontSize: 14,
            color: MOBILE_ACCENT_TEAL,
            transform: 'rotate(-45deg)',
        },
        // The big "N open enquiries" stat - new, matches EnquiryStatusOverview.tsx's own
        // statRow/statNumber/statLabel exactly. This is what the old row-based layout never
        // had: a single headline figure the proportion bar and metric cards both break down.
        statRow: {
            display: 'flex',
            alignItems: 'baseline',
            gap: 6,
            marginBottom: 12,
        },
        statNumber: {
            fontSize: 30,
            fontWeight: 800,
            color: '#0B1220',
            letterSpacing: -0.5,
            lineHeight: 1,
        },
        statLabel: {
            fontSize: 14.5,
            fontWeight: 600,
            color: '#5B6579',
        },
        statErrorText: {
            fontSize: 13,
            color: '#5B6579',
            marginBottom: 12,
        },
        // The segmented bar - new. Each segment's own width is proportional to its share via
        // flex-grow (a genuinely zero count collapses its own segment away, same as mobile -
        // no minimum floor that would misrepresent the real split).
        proportionBar: {
            display: 'flex',
            height: 8,
            borderRadius: 4,
            overflow: 'hidden',
            gap: 3,
            marginBottom: 8,
        },
        proportionSegment: {
            borderRadius: 4,
        },
        needAttentionCaption: {
            fontSize: 13,
            color: '#5B6579',
            marginBottom: 12,
        },
        needAttentionCount: {
            fontWeight: 800,
            color: '#0B1220',
        },
        // Replaces the old vertical icon-circle rows with the boxed metric-card grid mobile's
        // own card redesign uses - explicit request ("it has boxes inside where we show the
        // count"). Three equal-width cards in a row - CSS flex here does the equal-width job
        // outright, none of EnquiryStatusOverview.tsx's own JS-measured-width workaround is
        // needed (that exists purely for a React Native flex quirk with no web equivalent -
        // see that file's own metricCardWidth comment).
        metricCardsRow: {
            display: 'flex',
            gap: 8,
        },
        // Same "card sitting on a card" recipe mobile's own metric cards use: a uniformly
        // muted background + hairline border for every card regardless of state - only the
        // icon/count colour (and the optional aging pill) carry the active category tint, the
        // card chrome itself doesn't. Hover/focus treatment matches this app's own established
        // card-grid recipe (CLAUDE.md's "Card Grids" section) rather than inventing a new one.
        metricCard: {
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: MOBILE_SURFACE_MUTED,
            border: `1px solid ${MOBILE_BORDER}`,
            borderRadius: 16,
            padding: 12,
            transition: 'box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease',
            '@media (prefers-reduced-motion: reduce)': {
                transform: 'none !important',
            },
        },
        metricCardClickable: {
            cursor: 'pointer',
            '&:hover': {
                boxShadow: '0 4px 18px rgba(0,0,0,0.10)',
                borderColor: theme.palette.primary.light,
                transform: 'translateY(-1px)',
            },
        },
        metricCardCount: {
            fontSize: 22,
            fontWeight: 800,
            marginTop: 8,
        },
        metricCardLabel: {
            fontSize: 12,
            fontWeight: 600,
            color: '#5B6579',
            marginTop: 2,
            lineHeight: '15px',
        },
        agingPill: {
            marginTop: 8,
            alignSelf: 'flex-start',
            padding: '3px 8px',
            borderRadius: 999,
        },
        agingPillLabel: {
            fontSize: 10.5,
            fontWeight: 700,
        },
    })
);

// Explicit request: use this single query (no entityType/entityGuid params - implicitly
// scoped server-side to the signed-in user) for both the headline "N open enquiries" figure
// and the three-way health breakdown below, instead of enquiryHealth/summaryList (the chart
// query CrmEnquiresDataHealthChart.tsx itself still uses, untouched).
interface EnquiryQuickViewCountsData {
    statistics: {
        lead: {
            enquiryQuickViewCounts: {
                openEnquiries: number;
                uncontacted: number;
                noFollowUp3Plus: number;
                followUpOverdue: number;
                followUpOverdue3Plus: number;
                pendingMatterCreation: number;
            };
        };
    };
}

const EnquiryQuickViewCountsQuery = gql`
    query EnquiryQuickViewCounts {
        statistics {
            lead {
                enquiryQuickViewCounts {
                    openEnquiries
                    uncontacted
                    noFollowUp3Plus
                    followUpOverdue
                    followUpOverdue3Plus
                    pendingMatterCreation
                }
            }
        }
    }
`;

// Carried through browserHistory.push's own `state` (EnquirySummaryPage.tsx reads it back via
// useLocation().state on mount) so a metric card click actually filters the grid there, rather
// than just navigating to an unfiltered list - explicit request. Shape matches exactly what
// EnquirySummaryPage.tsx's own onApplySearch already accepts for these three fields, so it can
// be passed straight through with no translation step at the consuming end.
export interface QuickViewEnquiryFilter {
    /** Shown in EnquirySummaryPage.tsx's own clearable filter chip - e.g. "Not contacted yet". */
    label: string;
    status: IAutoCompleteItem[] | null;
    assignedTo: IAutoCompleteItem[] | null;
    isMyEnquiriesOnly: boolean;
    /** The enquirySummary GraphQL query's own dedicated overdue filter - explicit request to
     * use this instead of the old bare assignedTo/isMyEnquiriesOnly-only workaround, which
     * didn't actually filter to overdue enquiries at all (see onFollowUpOverdueClick below). */
    isFollowUpOverdue: boolean | null;
    /** Added for EnquiryFollowUpsDueWidget.tsx's own "View all" drilldown - mutually exclusive
     * with isMyEnquiriesOnly, same invariant EnquirySummaryPage.tsx's own toggle handlers
     * (onMyEnquiriesOnlyChange/onMyPersonalEnquiriesChange) already enforce. */
    isMyPersonalEnquiries: boolean;
    followUpDue: IAutoCompleteItem | null;
}

export const YourEnquiriesWidget: React.FC = () => {
    const classes = useStyles();

    // Once this query's own data has actually landed, its skeleton must never come back on a
    // later refetch - same fix, same reasoning, as EnquiryFollowUpsDueWidget.tsx's own
    // hasLoadedOnceRef: raw query.loading flips true again on Quick View's own header Refresh
    // button (refetch()), and wiring that straight into the loading gate was flashing this
    // card's whole skeleton on every refresh instead of just updating the numbers in place.
    const hasLoadedOnceRef = React.useRef(false);
    // Drives RefreshingIndicator - true only while a refresh triggered from Quick View's own
    // header button is actually in flight, so the widget can show a small "still working" cue
    // without hiding the data already on screen (see the header render below).
    const [isRefreshing, setIsRefreshing] = React.useState(false);

    // batchGroup opts this widget's query out of the default shared batch bucket (see
    // index.tsx's httpLink batchKey comment) - without it, Apollo's BatchHttpLink bundles
    // every Quick View widget's query fired in the same ~10ms mount window into one HTTP
    // request, so the fastest widget waits on the slowest one instead of rendering as soon as
    // its own data returns.
    const enquiryCountsQuery = useQuery<EnquiryQuickViewCountsData>(EnquiryQuickViewCountsQuery, {
        context: { batchGroup: 'quickView-yourEnquiries' },
    });

    React.useEffect(() => {
        if (enquiryCountsQuery.error) {
            showNotification('Failed to Fetch Your Enquiries', enquiryCountsQuery.error.message, 'error');
        }
    // tslint:disable-next-line: align
    }, [enquiryCountsQuery.error]);

    // Quick View's own header Refresh button (QuickViewDrawer.tsx) - refetchRef always holds
    // the latest refetch so the listener itself can be registered once on mount rather than
    // re-subscribing every render (Apollo doesn't guarantee `refetch`'s own identity is
    // stable across renders). Explicitly removed on unmount - EventSystem itself has no
    // built-in cleanup, and this widget mounts/unmounts every time the panel opens/closes.
    const refetchRef = React.useRef(enquiryCountsQuery.refetch);
    refetchRef.current = enquiryCountsQuery.refetch;
    React.useEffect(() => {
        // Explicit request: the Enquiry page's own create/update/delete subscriptions now emit
        // this same event scoped to 'enquiry' (see EnquirySummaryPage.tsx's onEnquiryCreated),
        // so this card also stays current without needing Quick View's header Refresh button.
        // Scope is undefined for that header button itself (refresh everything) and for any
        // other widget's own scope, so only bail out when a scope is given AND it isn't ours.
        const onRefresh = (scope?: string) => {
            if (scope && scope !== 'enquiry') {
                return;
            }
            runWithMinimumVisibleRefresh(setIsRefreshing, () => refetchRef.current());
        };
        EventSystem.on(QUICK_VIEW_REFRESH_EVENT, onRefresh);
        return () => { EventSystem.event.removeListener(QUICK_VIEW_REFRESH_EVENT, onRefresh); };
    // tslint:disable-next-line: align
    }, []);

    if (enquiryCountsQuery.data?.statistics?.lead?.enquiryQuickViewCounts) {
        hasLoadedOnceRef.current = true;
    }

    if (!hasLoadedOnceRef.current && enquiryCountsQuery.loading) {
        return (
            <div className={classes.card}>
                <SkeletonLoader variant="panel" columns={1} rows={4} />
            </div>
        );
    }

    if (enquiryCountsQuery.error || !enquiryCountsQuery.data?.statistics?.lead?.enquiryQuickViewCounts) {
        return null;
    }

    const {
        openEnquiries,
        uncontacted,
        noFollowUp3Plus,
        followUpOverdue,
        followUpOverdue3Plus,
        pendingMatterCreation,
    } = enquiryCountsQuery.data.statistics.lead.enquiryQuickViewCounts;

    // Same reading as EnquiryStatusOverview.tsx's own totalNeedingAttention/totalForBar -
    // deliberately excludes "unassigned" (not on mobile's own card either: for a self-scoped
    // "your enquiries" view, unassigned is a contradiction - they're yours, so they're
    // assigned).
    const needAttentionCount = uncontacted + followUpOverdue;
    const totalForBar = uncontacted + followUpOverdue + pendingMatterCreation;

    // Explicit request: "View all" now carries the same personal-enquiries scoping
    // (isMyEnquiriesOnly: false, isMyPersonalEnquiries: true) EnquiryFollowUpsDueWidget.tsx's
    // own "View all" (onOpenEnquiriesSummary) already drills down with, rather than landing on
    // an unfiltered grid. followUpDue stays null here - unlike that widget, this card isn't
    // scoped to follow-ups due, and the label is kept distinct so the two Quick View filter
    // chips aren't mistaken for each other.
    const onOpenEnquiriesClick = () => {
        browserHistory.push({
            pathname: linkToPage('enquiriesSummary'),
            state: {
                quickViewEnquiryFilter: {
                    label: 'Your personal enquiries',
                    status: null,
                    assignedTo: null,
                    isMyEnquiriesOnly: false,
                    isMyPersonalEnquiries: true,
                    followUpDue: null,
                    isFollowUpOverdue: null,
                } as QuickViewEnquiryFilter,
            },
        });
    };

    // Same filter shape as the mobile app's own EnquiryStatusOverview.tsx drilldown params
    // (RedView Mobile V2 src/components/EnquiryStatusOverview.tsx's handleNotContactedPressed/
    // handleFollowUpOverduePressed/handlePendingMatterPressed) - explicit request to match
    // that app's own drilldown filters exactly. `status` is matched as free text server-side,
    // not a status guid - confirmed via EnquirySummaryView.tsx's own tempStatus (`.map(item =>
    // item.label)`), the same field mobile's own comment says "matches the web's status param
    // exactly". `assignedTo` takes the signed-in person's guid directly (paired with
    // isMyEnquiriesOnly: false) rather than relying on the "My enquiries" toggle default alone,
    // since the two aren't guaranteed to scope identically - same reasoning mobile's own
    // comment gives. Follow-up overdue used to have no dedicated EnquiryList filter (matched to
    // the same bare assignedTo/isMyEnquiriesOnly pairing as "View all", so it never actually
    // filtered to overdue-only) - explicit request to switch to the query's own isFollowUpOverdue
    // parameter now that one exists, so onFollowUpOverdueClick below is the one card that
    // differs from mobile's own bare-assignedTo drilldown.
    const onNotContactedClick = () => {
        browserHistory.push({
            pathname: linkToPage('enquiriesSummary'),
            state: {
                quickViewEnquiryFilter: {
                    label: 'Not contacted yet',
                    status: [{ value: 'Assigned', label: 'Assigned' }],
                    assignedTo: [{ value: UserFeature.UserId, label: 'You' }],
                    isMyEnquiriesOnly: false,
                    isFollowUpOverdue: null,
                    isMyPersonalEnquiries: false,
                    followUpDue: null,
                } as QuickViewEnquiryFilter,
            },
        });
    };
    const onFollowUpOverdueClick = () => {
        browserHistory.push({
            pathname: linkToPage('enquiriesSummary'),
            state: {
                quickViewEnquiryFilter: {
                    label: 'Follow-up overdue',
                    status: null,
                    assignedTo: [{ value: UserFeature.UserId, label: 'You' }],
                    isMyEnquiriesOnly: false,
                    isFollowUpOverdue: true,
                    isMyPersonalEnquiries: false,
                    followUpDue: null,
                } as QuickViewEnquiryFilter,
            },
        });
    };
    const onPendingMatterClick = () => {
        browserHistory.push({
            pathname: linkToPage('enquiriesSummary'),
            state: {
                quickViewEnquiryFilter: {
                    label: 'Pending matter creation',
                    status: [{ value: 'Pending Matter', label: 'Pending Matter' }],
                    assignedTo: [{ value: UserFeature.UserId, label: 'You' }],
                    isMyEnquiriesOnly: false,
                    isFollowUpOverdue: null,
                    isMyPersonalEnquiries: false,
                    followUpDue: null,
                } as QuickViewEnquiryFilter,
            },
        });
    };

    interface MetricCardData {
        key: string;
        icon: React.ReactNode;
        soft: string;
        strong: string;
        count: number;
        label: string;
        agingCount?: number;
        agingCaption?: string;
        onClick?: () => void;
    }

    // agingCount/agingCaption drive the "Xd+" pill - noFollowUp3Plus/followUpOverdue3Plus are
    // enquiryQuickViewCounts' own equivalent of the old enquiryHealth query's noFollowup3Plus/
    // followupOverdue3Plus (see CrmEnquiresDataHealthChart.tsx), the subset of each card's count
    // that's aged past 3 days.
    const metricCards: MetricCardData[] = [
        {
            key: 'uncontacted',
            icon: <ChatBubbleOutlineIcon style={{ fontSize: 18 }} />,
            soft: MOBILE_STATUS_PENDING_SOFT,
            strong: MOBILE_STATUS_PENDING,
            count: uncontacted,
            label: 'not contacted yet',
            agingCount: noFollowUp3Plus,
            agingCaption: `${noFollowUp3Plus.toLocaleString()} for 3+ days`,
            onClick: uncontacted > 0 ? onNotContactedClick : undefined,
        },
        {
            key: 'followUpOverdue',
            icon: <AlarmOutlinedIcon style={{ fontSize: 18 }} />,
            soft: MOBILE_STATUS_URGENT_SOFT,
            strong: MOBILE_STATUS_URGENT,
            count: followUpOverdue,
            label: 'follow-up overdue',
            agingCount: followUpOverdue3Plus,
            agingCaption: `${followUpOverdue3Plus.toLocaleString()} for 3+ days`,
            onClick: followUpOverdue > 0 ? onFollowUpOverdueClick : undefined,
        },
        {
            key: 'pendingMatterCreation',
            icon: <WorkOutlineIcon style={{ fontSize: 18 }} />,
            soft: MOBILE_ACCENT_TEAL_SOFT,
            strong: MOBILE_ACCENT_TEAL,
            count: pendingMatterCreation,
            label: 'pending matter creation',
            onClick: pendingMatterCreation > 0 ? onPendingMatterClick : undefined,
        },
    ];

    return (
        <div className={classes.card}>
            <div className={classes.wash} />
            <svg width={112} height={112} viewBox="0 0 24 24" className={classes.watermark}>
                <path d={PULSE_PATH.split(' H48')[0]} stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
            <div className={classes.content}>
                <div className={classes.headerRow}>
                    <div className={classes.headerLeft}>
                        <div className={classes.headerIconChip}>
                            <svg width={24} height={24} viewBox="0 0 24 24" style={{ overflow: 'hidden' }}>
                                <path
                                    className={classes.pulseTrace}
                                    d={PULSE_PATH}
                                    stroke={MOBILE_ACCENT_TEAL}
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    fill="none"
                                />
                            </svg>
                        </div>
                        <Typography className={classes.heading}>Your Personal Enquiries</Typography>
                        {isRefreshing && <RefreshingIndicator />}
                    </div>
                    <button type="button" className={classes.viewAllLink} onClick={onOpenEnquiriesClick}>
                        <Typography className={classes.viewAllLabel}>View all</Typography>
                        <ArrowForwardIcon className={classes.viewAllIcon} />
                    </button>
                </div>

                {/* No separate loading/error branch needed here anymore - openEnquiries comes
                    from the same enquiryCountsQuery already gated above (skeleton while
                    loading, null render on error), unlike the old separate openEnquiriesQuery. */}
                <div className={classes.statRow}>
                    <Typography className={classes.statNumber}>{openEnquiries.toLocaleString()}</Typography>
                    <Typography className={classes.statLabel}>open {openEnquiries === 1 ? 'enquiry' : 'enquiries'}</Typography>
                </div>

                {totalForBar > 0 && (
                    <div className={classes.proportionBar}>
                        <div className={classes.proportionSegment} style={{ flexGrow: uncontacted, flexBasis: 0, backgroundColor: MOBILE_STATUS_PENDING }} />
                        <div className={classes.proportionSegment} style={{ flexGrow: followUpOverdue, flexBasis: 0, backgroundColor: MOBILE_STATUS_URGENT }} />
                        <div className={classes.proportionSegment} style={{ flexGrow: pendingMatterCreation, flexBasis: 0, backgroundColor: MOBILE_ACCENT_TEAL }} />
                    </div>
                )}
                <Typography className={classes.needAttentionCaption}>
                    <span className={classes.needAttentionCount}>{needAttentionCount.toLocaleString()} </span>
                    need attention
                </Typography>

                <div className={classes.metricCardsRow}>
                    {metricCards.map((card) => {
                        const isZero = card.count === 0;
                        const strong = isZero ? MOBILE_TEXT_MUTED : card.strong;
                        const showAging = !isZero && card.agingCount !== undefined && card.agingCount > 0;
                        return (
                            <div
                                key={card.key}
                                className={`${classes.metricCard} ${card.onClick ? classes.metricCardClickable : ''}`}
                                onClick={card.onClick}
                                role={card.onClick ? 'button' : undefined}
                                tabIndex={card.onClick ? 0 : undefined}
                                onKeyDown={card.onClick ? (e) => { if (e.key === 'Enter') { card.onClick!(); } } : undefined}
                            >
                                <span style={{ color: strong, display: 'flex' }}>{card.icon}</span>
                                <Typography className={classes.metricCardCount} style={{ color: strong }}>{card.count.toLocaleString()}</Typography>
                                <Typography className={classes.metricCardLabel}>{card.label}</Typography>
                                {showAging && (
                                    <div className={classes.agingPill} style={{ backgroundColor: card.soft }}>
                                        <Typography className={classes.agingPillLabel} style={{ color: strong }}>{card.agingCaption}</Typography>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
